import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, BadgeCheck, CalendarClock, ChevronDown, ChevronRight, FileCheck2, HeartHandshake, Pencil, Plus, RefreshCcw, Save, ShieldCheck, Trash2, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EditorModal } from "@/components/EditorModal";
import { ComplianceDocumentForm, ComplianceDocumentRow } from "@/components/credentials/ComplianceDocumentsPanel";
import { CredentialDocuments } from "@/components/credentials/CredentialDocuments";
import { CredentialFields } from "@/components/credentials/CredentialFields";
import { MedicaidForm, MedicaidRow } from "@/components/credentials/MedicaidPanel";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyCredential = {
  provider_id: "",
  credential_type: "",
  expiration_date: "",
  required_for_assignment: true,
  document_status: "Pending Verification",
  license_number: "",
  issuing_state: "",
  issue_date: "",
  board_lookup_url: "",
};
const MANAGE_ROLES = ["system_administrator", "operations", "hr"];
const DOCUMENT_ROLES = ["system_administrator", "hr"];
const EDIT_FIELDS = ["provider_id", "credential_type", "expiration_date", "required_for_assignment", "document_status", "license_number", "issuing_state", "issue_date", "board_lookup_url"];

function credentialRiskClass(risk) {
  return `credential-risk credential-risk-${risk.toLowerCase()}`;
}

function groupByProvider(records, medicaid = [], documents = [], providers = []) {
  const groups = new Map();
  const ensure = (record) => {
    const group = groups.get(record.provider_id) || { provider_id: record.provider_id, provider_name: record.provider_name, provider_discipline: record.provider_discipline, designations: "", records: [], medicaid: [], documents: [], soonest: null, blocked: 0, unverified: 0 };
    groups.set(record.provider_id, group);
    return group;
  };
  for (const record of records) {
    const group = ensure(record);
    group.records.push(record);
    if (record.expiration_date && (!group.soonest || record.expiration_date < group.soonest)) group.soonest = record.expiration_date;
    group.blocked += record.blocks_assignment ? 1 : 0;
    group.unverified += record.document_status !== "Verified" ? 1 : 0;
  }
  for (const record of medicaid) ensure(record).medicaid.push(record);
  for (const record of documents) ensure(record).documents.push(record);
  for (const provider of providers) {
    if (!provider.designations) continue;
    ensure({ provider_id: provider.id, provider_name: provider.name, provider_discipline: provider.discipline }).designations = provider.designations;
  }
  return [...groups.values()].sort((a, b) => (a.soonest || "9999").localeCompare(b.soonest || "9999") || a.provider_name.localeCompare(b.provider_name));
}

const GroupRows = ({ group, expanded, onToggle, children }) => (
  <>
    <tr className="credential-group-row" data-testid={`credential-group-${group.provider_id}`} onClick={onToggle}>
      <td colSpan={8}>
        <b>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {group.provider_name}</b>
        <span data-testid={`credential-group-summary-${group.provider_id}`}>
          {group.provider_discipline || "Provider"}{group.designations ? ` · ${group.designations}` : ""} · {group.records.length} credential{group.records.length === 1 ? "" : "s"} · soonest expiration {group.soonest || "—"}
          {group.blocked ? ` · ${group.blocked} blocking` : ""}{group.unverified ? ` · ${group.unverified} not verified` : ""}
          {group.medicaid.length ? ` · Medicaid ${group.medicaid.map((m) => `${m.jurisdiction} ${m.effective_status}`).join(", ")}` : ""}
          {group.documents.length ? ` · ${group.documents.length} compliance document${group.documents.length === 1 ? "" : "s"}` : ""}
        </span>
      </td>
    </tr>
    {expanded && children}
  </>
);

function calendarNotice(invite, hrEmail) {
  if (!invite) return "";
  if (invite.status === "sent") return ` HR calendar ${invite.action === "deleted" ? "cancellation" : "invite"} sent to ${hrEmail}.`;
  return ` HR calendar invite not delivered (${invite.error}).`;
}

const CalendarState = ({ invite, testId }) => {
  if (!invite) return <span className="credential-calendar-state is-none" data-testid={testId}>—</span>;
  const label = { sent: "Invite sent", suppressed: "Live site only", failed: "Not delivered" }[invite.status] || invite.status;
  return (
    <span className={`credential-calendar-state is-${invite.status}`} data-testid={testId} title={invite.error || `Sent ${new Date(invite.sent_at).toLocaleString()}`}>
      <CalendarClock size={12} /> {label}
    </span>
  );
};

export default function CredentialIntelligencePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyCredential);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const canManage = MANAGE_ROLES.includes(user?.role);
  const canUploadDocs = DOCUMENT_ROLES.includes(user?.role);
  const [expanded, setExpanded] = useState(() => new Set());
  const [compliance, setCompliance] = useState({ catalog: null, medicaid: [], documents: [], customTypes: [] });
  const [medicaidModal, setMedicaidModal] = useState(null);
  const [documentModal, setDocumentModal] = useState(null);
  const groupedRecords = groupByProvider(data?.records || [], compliance.medicaid, compliance.documents, data?.providers || []);

  const loadCompliance = useCallback(async () => {
    if (!canManage) return;
    try {
      const [catalogResponse, medicaidResponse, documentsResponse, requirementsResponse] = await Promise.all([
        api.get("/compliance/catalog"),
        api.get("/compliance/medicaid"),
        canUploadDocs ? api.get("/compliance/documents") : Promise.resolve({ data: [] }),
        api.get("/compliance/requirements"),
      ]);
      const customTypes = [...new Set(requirementsResponse.data.clients.flatMap((c) => c.requirements.custom.map((item) => item.name)))];
      setCompliance({ catalog: catalogResponse.data, medicaid: medicaidResponse.data, documents: documentsResponse.data, customTypes });
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [canManage, canUploadDocs]);

  useEffect(() => {
    loadCompliance();
  }, [loadCompliance]);

  function toggleGroup(providerId) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  }

  async function runReverification() {
    try {
      const response = await api.post("/credential-intelligence/reverification/run");
      setNotice(`Re-verification check complete: ${response.data.reminders} reminder(s), ${response.data.unverified} marked Unverified.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function load() {
    try {
      const response = await api.get("/credential-intelligence");
      setData(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function submit(event) {
    event.preventDefault();
    try {
      const response = await api.post("/credential-intelligence", form);
      setForm(emptyCredential);
      setShowForm(false);
      setNotice(`Credential added for ${response.data.provider_name}.${calendarNotice(response.data.calendar_invite, data.hr_calendar_email)}`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveCredential(event) {
    event.preventDefault();
    try {
      const response = await api.put(`/credential-intelligence/${editing.id}`, Object.fromEntries(EDIT_FIELDS.map((key) => [key, editing[key] ?? ""])));
      setEditing(null);
      setNotice(`Credential updated for ${response.data.provider_name}.${calendarNotice(response.data.calendar_invite, data.hr_calendar_email)}`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function removeCredential(record) {
    try {
      const response = await api.delete(`/credential-intelligence/${record.id}`);
      setDeleteCandidate(null);
      setNotice(`${record.credential_type} for ${record.provider_name} deleted.${calendarNotice(response.data.calendar_invite, data.hr_calendar_email)}`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="credential-intelligence-shell">
      <AppSidebar activeItem="Credentials" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page credential-page" data-testid="credential-intelligence-page">
          <section className="page-intro credential-intro">
            <div>
              <p className="eyebrow">PROVIDER READINESS CONTROL</p>
              <h1>Credentials</h1>
              <p className="page-subtitle">
                Track provider eligibility before coverage is committed to a client.
              </p>
            </div>
            {canManage && (
              <div className="button-row">
                {canUploadDocs && (
                  <button className="button button-ghost" data-testid="run-reverification-button" type="button" onClick={runReverification}>
                    <RefreshCcw size={16} /> Run re-verification check
                  </button>
                )}
                {compliance.catalog && (
                  <button className="button button-ghost" data-testid="add-medicaid-button" type="button" onClick={() => setMedicaidModal({})}>
                    <HeartHandshake size={16} /> Add Medicaid enrollment
                  </button>
                )}
                {canUploadDocs && compliance.catalog && (
                  <button className="button button-ghost" data-testid="add-compliance-document-button" type="button" onClick={() => setDocumentModal({})}>
                    <FileCheck2 size={16} /> Add compliance document
                  </button>
                )}
                <button
                  className="button button-primary"
                  data-testid="add-provider-credential-button"
                  type="button"
                  onClick={() => setShowForm(true)}
                >
                  <Plus size={16} /> Add credential
                </button>
              </div>
            )}
          </section>

          {error && (
            <p className="form-message error-message" data-testid="credential-intelligence-error">
              {error}
            </p>
          )}
          {notice && (
            <p className="form-message success-message" data-testid="credential-intelligence-notice">
              {notice}
            </p>
          )}
          {!data && !error && (
            <p className="projection-loading" data-testid="credential-intelligence-loading">
              Loading…
            </p>
          )}

          {data && (
            <>
              <section className="credential-summary-grid" data-testid="credential-summary-grid">
                <CredentialMetric
                  icon={BadgeCheck}
                  label="Tracked credentials"
                  value={data.summary.tracked}
                  testId="credential-tracked-count"
                />
                <CredentialMetric
                  icon={AlertTriangle}
                  label="Critical alerts"
                  value={data.summary.critical}
                  tone="red"
                  testId="credential-critical-count"
                />
                <CredentialMetric
                  icon={ShieldCheck}
                  label="Assignment blocks"
                  value={data.summary.assignment_blocks}
                  tone="yellow"
                  testId="credential-block-count"
                />
                <CredentialMetric
                  icon={AlertTriangle}
                  label="Advance alerts"
                  value={data.summary.upcoming}
                  testId="credential-upcoming-count"
                />
              </section>

              <section
                className="credential-schedule"
                data-testid="credential-notification-schedule"
              >
                <ShieldCheck size={17} />
                <span>
                  Operations alerts are created at {data.notification_schedule_days.join(", ")}-day
                  expiry thresholds.
                  {data.hr_calendar_email && (
                    <>
                      {" "}Each expiry date is also sent as a calendar invite to{" "}
                      <b data-testid="credential-hr-calendar-email">{data.hr_calendar_email}</b> with matching reminders.
                    </>
                  )}
                </span>
              </section>

              {showForm && (
                <form
                  className="credential-form"
                  data-testid="credential-create-form"
                  onSubmit={submit}
                >
                  <CredentialFields value={form} onChange={setForm} providers={data.providers} prefix="credential" />
                  <button
                    className="button button-primary"
                    data-testid="save-provider-credential-button"
                  >
                    <ShieldCheck size={16} /> Save credential
                  </button>
                </form>
              )}

              {editing && (
                <EditorModal onClose={() => setEditing(null)} testId="credential-editor-modal">
                <section className="inline-edit-panel" data-testid="credential-editor">
                  <div className="section-heading">
                    <div>
                      <h2>Edit credential</h2>
                      <p>Changes to the provider, credential or expiry re-send the HR calendar invite.</p>
                    </div>
                    <button
                      className="projection-icon-button"
                      data-testid="close-credential-editor"
                      type="button"
                      onClick={() => setEditing(null)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <form className="inline-edit-form" onSubmit={saveCredential}>
                    {error && <p className="form-message error-message" data-testid="credential-editor-error">{error}</p>}
                    <CredentialFields value={editing} onChange={setEditing} providers={data.providers} prefix="edit-credential" />
                    {editing.verified_by_name && <p className="pairing-meta" data-testid="edit-credential-verified-by">Verified {editing.verified_date} by {editing.verified_by_name}</p>}
                    <CredentialDocuments record={editing} canUpload={canUploadDocs} onChanged={async () => { await load(); const fresh = (await api.get("/credential-intelligence")).data.records.find((r) => r.id === editing.id); if (fresh) setEditing({ ...editing, ...fresh }); }} />
                    <button className="button button-primary" data-testid="save-credential-button">
                      <Save size={16} /> Save credential
                    </button>
                  </form>
                </section>
                </EditorModal>
              )}

              {medicaidModal && compliance.catalog && (
                <EditorModal onClose={() => setMedicaidModal(null)} testId="medicaid-modal">
                  <section className="inline-edit-panel" data-testid="medicaid-editor">
                    <div className="section-heading">
                      <div>
                        <h2>{medicaidModal.id ? "Edit Medicaid enrollment" : "Add Medicaid enrollment"}</h2>
                        <p>HR is alerted {compliance.catalog.medicaid_alert_days.join(", ")} days before the revalidation due date.</p>
                      </div>
                      <button className="projection-icon-button" data-testid="close-medicaid-editor" type="button" onClick={() => setMedicaidModal(null)}><X size={16} /></button>
                    </div>
                    <MedicaidForm catalog={compliance.catalog} initial={medicaidModal} providers={data.providers} onCancel={() => setMedicaidModal(null)} onSaved={async (saved) => { setMedicaidModal(null); setNotice(`Medicaid enrollment saved for ${saved.provider_name}.`); await loadCompliance(); }} />
                  </section>
                </EditorModal>
              )}

              {documentModal && compliance.catalog && (
                <EditorModal onClose={() => setDocumentModal(null)} testId="compliance-document-modal">
                  <section className="inline-edit-panel" data-testid="compliance-document-editor">
                    <div className="section-heading">
                      <div>
                        <h2>{documentModal.id ? "Edit compliance document" : "Add compliance document"}</h2>
                        <p>Re-check alerts go to HR {compliance.catalog.document_alert_days.join(" and ")} days before expiry. Drug screen and TB results are stored apart and visible to the System Administrator and HR only.</p>
                      </div>
                      <button className="projection-icon-button" data-testid="close-compliance-document-editor" type="button" onClick={() => setDocumentModal(null)}><X size={16} /></button>
                    </div>
                    <ComplianceDocumentForm catalog={compliance.catalog} customTypes={compliance.customTypes} initial={documentModal} providers={data.providers} onCancel={() => setDocumentModal(null)} onSaved={async (saved) => { setDocumentModal(null); setNotice(`${saved.document_type} saved for ${saved.provider_name}.`); await loadCompliance(); }} />
                  </section>
                </EditorModal>
              )}

              {deleteCandidate && (
                <div className="projection-delete-confirm credential-delete-confirm" data-testid="credential-delete-confirmation">
                  <span>
                    Delete <strong>{deleteCandidate.credential_type}</strong> for {deleteCandidate.provider_name}? The record is archived, the
                    deletion is logged and a cancellation is sent to the HR calendar.
                  </span>
                  <div>
                    <button
                      className="button button-secondary"
                      data-testid="cancel-delete-credential-button"
                      type="button"
                      onClick={() => setDeleteCandidate(null)}
                    >
                      Keep credential
                    </button>
                    <button
                      className="button projection-confirm-delete-button"
                      data-testid="confirm-delete-credential-button"
                      type="button"
                      onClick={() => removeCredential(deleteCandidate)}
                    >
                      <Trash2 size={14} /> Delete credential
                    </button>
                  </div>
                </div>
              )}

              <section className="credential-table-wrap" data-testid="credential-records-table">
                <table>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Credential</th>
                      <th>Expires</th>
                      <th>Verified</th>
                      <th>Risk</th>
                      <th>Assignment</th>
                      <th>HR calendar</th>
                      {canManage && <th aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRecords.map((group) => (
                      <GroupRows key={group.provider_id} group={group} expanded={expanded.has(group.provider_id)} onToggle={() => toggleGroup(group.provider_id)}>
                        {group.records.map((record) => (
                      <tr data-testid={`credential-record-${record.id}`} key={record.id} className="credential-group-child">
                        <td>
                          <b>{record.provider_name}</b>
                          <span>{record.provider_discipline}</span>
                        </td>
                        <td>
                          {record.credential_type}
                          <span data-testid={`credential-status-${record.id}`}>{record.document_status}{record.license_number ? ` · #${record.license_number}` : ""}{record.issuing_state ? ` · ${record.issuing_state}` : ""}</span>
                        </td>
                        <td>
                          {record.expiration_date}
                          <span>
                            {record.days_remaining === null
                              ? "Not tracked"
                              : `${record.days_remaining} days remaining`}
                          </span>
                        </td>
                        <td data-testid={`credential-verified-${record.id}`}>
                          {record.verified_date ? record.verified_date : "—"}
                          <span>
                            {record.verified_by_name ? `by ${record.verified_by_name}` : ""}
                            {record.reverification_days_remaining !== null && record.reverification_days_remaining !== undefined
                              ? ` · re-verify in ${record.reverification_days_remaining} days`
                              : ""}
                          </span>
                        </td>
                        <td>
                          <em className={credentialRiskClass(record.risk)}>{record.risk}</em>
                        </td>
                        <td>
                          {record.blocks_assignment ? (
                            <span className="credential-blocked">Blocked</span>
                          ) : record.required_for_assignment ? (
                            "Required"
                          ) : (
                            "Optional"
                          )}
                        </td>
                        <td>
                          <CalendarState invite={record.calendar_invite} testId={`credential-calendar-${record.id}`} />
                        </td>
                        {canManage && (
                          <td className="credential-actions">
                            <button
                              className="table-edit-button"
                              data-testid={`edit-credential-${record.id}`}
                              type="button"
                              onClick={() => setEditing(record)}
                            >
                              <Pencil size={14} /> Edit
                            </button>
                            <button
                              className="table-edit-button table-delete-button"
                              data-testid={`delete-credential-${record.id}`}
                              type="button"
                              onClick={() => setDeleteCandidate(record)}
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                        ))}
                        {group.medicaid.map((record) => (
                          <MedicaidRow key={record.id} canManage={canManage} record={record} onEdit={(row) => setMedicaidModal(row)} onDeleted={async (row) => { setNotice(`Medicaid enrollment removed for ${row.provider_name}.`); await loadCompliance(); }} />
                        ))}
                        {group.documents.map((record) => (
                          <ComplianceDocumentRow key={record.id} record={record} onEdit={(row) => setDocumentModal(row)} onDeleted={async (row) => { setNotice(`${row.document_type} removed for ${row.provider_name}.`); await loadCompliance(); }} />
                        ))}
                      </GroupRows>
                    ))}
                  </tbody>
                </table>
                {!data.records.length && (
                  <p className="operations-empty-state" data-testid="credential-empty-state">
                    No credential records yet. Add verified requirements to activate expiry
                    monitoring and assignment protection.
                  </p>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function CredentialMetric({ icon: Icon, label, value, tone = "teal", testId }) {
  return (
    <article className={`credential-metric credential-metric-${tone}`} data-testid={testId}>
      <Icon size={18} />
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
